import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Thermometer, Sparkles, MapPin, Wand2, ChevronDown } from "lucide-react";
import { useI18n } from "@/i18n";

const VIBE_META = {
  couple: { it: "Coppie", en: "Couples" },
  family: { it: "Famiglie", en: "Families" },
  friends: { it: "Amici", en: "Friends" },
  premium: { it: "Premium", en: "Premium" },
  sport: { it: "Sportivi", en: "Active" },
  nature: { it: "Natura", en: "Nature" },
};

const MONTHS_IT = ["Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott"];
const MONTHS_EN = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct"];
const MONTH_KEYS = ["april", "may", "june", "july", "august", "september", "october"];

function bestPeriodSummary(period, lang) {
  if (!period) return "";
  const months = lang === "en" ? MONTHS_EN : MONTHS_IT;
  const top = MONTH_KEYS.map((k, i) => (["exceptional", "excellent"].includes(period[k]) ? months[i] : null)).filter(Boolean);
  if (top.length >= 2) return `${top[0]} – ${top[top.length - 1]}`;
  const good = MONTH_KEYS.map((k, i) => (["exceptional", "excellent", "good"].includes(period[k]) ? months[i] : null)).filter(Boolean);
  if (good.length >= 2) return `${good[0]} – ${good[good.length - 1]}`;
  return lang === "en" ? "Year-round" : "Tutto l'anno";
}

export default function Destinations() {
  const { lang, localizePath } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/destinations/public?lang=${lang}`)
      .then((r) => { if (!cancel) setItems(r.data || []); })
      .catch(() => {})
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [lang]);

  const EN = lang === "en";

  return (
    <div data-testid="destinations-page" className="bg-white">
      {/* HERO */}
      <section className="relative pt-32 pb-16">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sand via-white to-white" />
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{EN ? "Sailing destinations" : "Destinazioni"}</p>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.05]">
            {EN ? "A curated atlas of" : "Un atlante curato delle"}
            <br />
            <em className="italic text-gold font-medium">{EN ? "Mediterranean sailing." : "vacanze in barca."}</em>
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-ink leading-relaxed">
            {EN
              ? "Eighteen destinations across the Mediterranean and beyond, each presented with the eye of a concierge — what makes it unique, the right season to sail, indicative budget and a suggested itinerary."
              : "Diciotto destinazioni nel Mediterraneo e oltre. Per ognuna trovi un ritratto curato dal nostro team: cosa la rende unica, il periodo migliore per andarci, budget realistici e un itinerario suggerito."}
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="pb-12">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <DestinationMatch items={items} lang={lang} localizePath={localizePath} navigate={navigate} />
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <p className="label-eyebrow">{EN ? "All destinations" : "Tutte le destinazioni"}</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-navy leading-tight">
            {EN ? "Explore our curated atlas" : "Esplora l'atlante completo"}
          </h2>
          {loading ? (
            <div className="text-center text-ink/60 py-16">{EN ? "Loading…" : "Caricamento…"}</div>
          ) : (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((d) => (
                <Link
                  key={d.id}
                  to={localizePath(`/${EN ? "destinations" : "destinazioni"}/${d.slug}`)}
                  data-testid={`dest-card-${d.slug}`}
                  className="group block bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden hover:border-navy/40 hover:shadow-xl transition-all"
                >
                  <div className="relative h-52 overflow-hidden bg-sand">
                    <img
                      src={d.hero_image}
                      alt={d.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/10 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 text-white">
                      <p className="text-[10px] uppercase tracking-wider text-gold font-semibold">{d.type_label}</p>
                      <h3 className="mt-1 font-serif text-2xl leading-tight">{d.name}</h3>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-2 gap-3 text-xs text-ink">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-gold" />
                        <span>{bestPeriodSummary(d.best_period, lang)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Thermometer className="h-3.5 w-3.5 text-gold" />
                        <span>{d.climate?.air_min}–{d.climate?.air_max}°C</span>
                      </div>
                      <div className="flex items-center gap-1.5 col-span-2">
                        <MapPin className="h-3.5 w-3.5 text-gold" />
                        <span>{d.country}</span>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {(d.vibes || []).slice(0, 4).map((v) => (
                        <span key={v} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sand text-ink/70">
                          {VIBE_META[v]?.[lang] || v}
                        </span>
                      ))}
                    </div>
                    <div className="mt-5 inline-flex items-center text-xs font-semibold text-navy group-hover:text-gold transition">
                      {EN ? "Discover more" : "Scopri di più"}
                      <ArrowRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-navy text-white py-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p className="label-eyebrow text-gold">{EN ? "Need help choosing?" : "Non sai quale scegliere?"}</p>
            <h2 className="mt-3 font-serif text-3xl">{EN ? "Speak with your concierge." : "Parla con un consulente Seamatch."}</h2>
            <p className="mt-3 text-white/70 max-w-xl">
              {EN ? "Share a short brief — we'll quietly suggest the destination that fits your party and the season." : "Raccontaci cosa cerchi: in 24 ore ti diciamo la destinazione più adatta al tuo gruppo e al periodo scelto."}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="dest-cta-search"
              onClick={() => navigate(localizePath("/search"))}
              className="bg-white text-navy hover:bg-sand rounded-full h-12 px-6"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {EN ? "Brief my concierge" : "Inizia la richiesta"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// DESTINATION MATCH — consulenza premium (non-quiz) in 4 domande → 2-3 destinazioni
// ============================================================================
const MATCH_OPTIONS = {
  with: [
    { id: "solo",    label_it: "Solo",     label_en: "Solo" },
    { id: "couple",  label_it: "Coppia",   label_en: "Couple" },
    { id: "family",  label_it: "Famiglia", label_en: "Family" },
    { id: "friends", label_it: "Amici",    label_en: "Friends" },
  ],
  budget: [
    { id: "low",     label_it: "Sotto 1.000 €/persona",    label_en: "Under €1,000 / guest" },
    { id: "mid",     label_it: "1.000–2.000 €/persona",    label_en: "€1,000–€2,000 / guest" },
    { id: "high",    label_it: "Premium (oltre 2.000 €)",  label_en: "Premium (above €2,000)" },
  ],
  vibe: [
    { id: "relax",   label_it: "Relax",      label_en: "Relax" },
    { id: "nature",  label_it: "Natura",     label_en: "Nature" },
    { id: "culture", label_it: "Cultura",    label_en: "Culture" },
    { id: "luxury",  label_it: "Lusso",      label_en: "Luxury" },
    { id: "nightlife", label_it: "Nightlife", label_en: "Nightlife" },
    { id: "mixed",   label_it: "Misto",      label_en: "A mix of everything" },
  ],
  period: [
    { id: "spring",  label_it: "Primavera (Apr–Mag)",     label_en: "Spring (Apr–May)" },
    { id: "early",   label_it: "Inizio estate (Giu)",     label_en: "Early summer (Jun)" },
    { id: "peak",    label_it: "Alta stagione (Lug–Ago)", label_en: "Peak summer (Jul–Aug)" },
    { id: "late",    label_it: "Fine estate (Set–Ott)",   label_en: "Late summer (Sep–Oct)" },
  ],
};

// Mapping slug → tags for scoring
const DEST_SCORING = {
  "sicilia": { vibe: ["culture", "mixed", "relax"], budget: ["mid", "high"], with: ["couple", "family", "friends"] },
  "isole-eolie": { vibe: ["nature", "relax", "luxury"], budget: ["mid", "high"], with: ["couple", "friends"] },
  "isole-egadi": { vibe: ["nature", "relax"], budget: ["low", "mid"], with: ["couple", "family"] },
  "isola-d-elba": { vibe: ["nature", "relax"], budget: ["low", "mid"], with: ["family", "couple"] },
  "isole-pontine": { vibe: ["relax", "nature"], budget: ["mid"], with: ["couple", "friends"] },
  "ischia": { vibe: ["relax", "culture", "luxury"], budget: ["mid", "high"], with: ["couple", "family"] },
  "capri": { vibe: ["luxury", "culture", "nightlife"], budget: ["high"], with: ["couple", "friends"] },
  "costiera-amalfitana": { vibe: ["culture", "luxury", "relax"], budget: ["high"], with: ["couple"] },
  "calabria": { vibe: ["nature", "relax"], budget: ["low", "mid"], with: ["family", "friends"] },
  "sardegna": { vibe: ["luxury", "relax", "nature", "mixed"], budget: ["mid", "high"], with: ["couple", "family", "friends"] },
  "corsica": { vibe: ["nature", "luxury"], budget: ["mid", "high"], with: ["couple", "family"] },
  "baleari": { vibe: ["nightlife", "luxury", "mixed"], budget: ["mid", "high"], with: ["friends", "couple"] },
  "grecia": { vibe: ["mixed", "culture", "nightlife", "relax"], budget: ["low", "mid", "high"], with: ["couple", "friends", "family", "solo"] },
  "creta": { vibe: ["culture", "nature"], budget: ["mid"], with: ["family", "couple"] },
  "croazia": { vibe: ["mixed", "nightlife", "culture"], budget: ["low", "mid"], with: ["friends", "family"] },
  "seychelles": { vibe: ["luxury", "relax", "nature"], budget: ["high"], with: ["couple"] },
  "caraibi": { vibe: ["luxury", "relax"], budget: ["high"], with: ["couple", "friends"] },
  "thailandia": { vibe: ["culture", "luxury", "nature"], budget: ["mid", "high"], with: ["couple", "family", "friends"] },
};

// Periods mapping (peak excluded for tropics)
const PERIOD_MONTH_KEYS = {
  spring: ["april", "may"],
  early: ["june"],
  peak: ["july", "august"],
  late: ["september", "october"],
};

function scoreDestination(dest, answers) {
  const s = DEST_SCORING[dest.slug] || DEST_SCORING[Object.keys(DEST_SCORING).find(k => dest.slug?.includes(k))] || null;
  if (!s) return 0;
  let score = 0;
  if (s.with.includes(answers.with)) score += 3;
  if (s.budget.includes(answers.budget)) score += 3;
  if (s.vibe.includes(answers.vibe)) score += 4;
  // period: check if best_period has at least one excellent/good in chosen months
  const months = PERIOD_MONTH_KEYS[answers.period] || [];
  const periodHit = months.some((m) => ["exceptional", "excellent", "good"].includes(dest.best_period?.[m]));
  if (periodHit) score += 2;
  return score;
}

function DestinationMatch({ items, lang, localizePath, navigate }) {
  const EN = lang === "en";
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({ with: null, budget: null, vibe: null, period: null });

  const complete = answers.with && answers.budget && answers.vibe && answers.period;

  const results = useMemo(() => {
    if (!complete || !items?.length) return [];
    const scored = items
      .map((d) => ({ d, s: scoreDestination(d, answers) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 3);
    return scored.map((x) => x.d);
  }, [items, answers, complete]);

  const Option = ({ groupKey, opt }) => {
    const active = answers[groupKey] === opt.id;
    return (
      <button
        data-testid={`match-${groupKey}-${opt.id}`}
        type="button"
        onClick={() => setAnswers({ ...answers, [groupKey]: opt.id })}
        className={`px-4 py-2.5 rounded-full text-sm font-medium border transition ${
          active ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E2E8F0] hover:border-navy/40"
        }`}
      >
        {opt[`label_${lang}`]}
      </button>
    );
  };

  const RANK_LABELS = ["1°", "2°", "3°"];

  return (
    <div className="rounded-2xl border-2 border-gold/30 bg-gradient-to-br from-sand/60 via-white to-white overflow-hidden">
      <button
        type="button"
        data-testid="match-toggle"
        onClick={() => setOpen(!open)}
        className="w-full px-6 py-5 flex items-center justify-between text-left"
      >
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 rounded-full bg-navy text-white inline-flex items-center justify-center shrink-0">
            <Wand2 className="h-5 w-5 text-gold" />
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gold font-semibold">{EN ? "Concierge tool" : "Strumento di consulenza"}</p>
            <h3 className="mt-1 font-serif text-xl sm:text-2xl text-navy leading-tight">
              {EN ? "Find your perfect destination" : "Trova la destinazione perfetta"}
            </h3>
            <p className="mt-0.5 text-xs text-ink/60">
              {EN
                ? "4 quick questions · we shortlist your top 3 destinations"
                : "4 domande veloci · ti consigliamo le 3 destinazioni più adatte"}
            </p>
          </div>
        </div>
        <ChevronDown className={`h-5 w-5 text-navy transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-6 pb-7 space-y-5">
          {/* WITH */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-ink/60 font-semibold mb-2">{EN ? "Who are you sailing with?" : "Con chi viaggi?"}</p>
            <div className="flex flex-wrap gap-2">{MATCH_OPTIONS.with.map((o) => <Option key={o.id} groupKey="with" opt={o} />)}</div>
          </div>
          {/* BUDGET */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-ink/60 font-semibold mb-2">{EN ? "Indicative budget" : "Budget indicativo"}</p>
            <div className="flex flex-wrap gap-2">{MATCH_OPTIONS.budget.map((o) => <Option key={o.id} groupKey="budget" opt={o} />)}</div>
          </div>
          {/* VIBE */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-ink/60 font-semibold mb-2">{EN ? "Relax or adventure?" : "Relax o avventura?"}</p>
            <div className="flex flex-wrap gap-2">{MATCH_OPTIONS.vibe.map((o) => <Option key={o.id} groupKey="vibe" opt={o} />)}</div>
          </div>
          {/* PERIOD */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-ink/60 font-semibold mb-2">{EN ? "When would you like to sail?" : "Periodo preferito"}</p>
            <div className="flex flex-wrap gap-2">{MATCH_OPTIONS.period.map((o) => <Option key={o.id} groupKey="period" opt={o} />)}</div>
          </div>

          {/* RESULTS */}
          {complete && (
            <div data-testid="match-results" className="mt-4 pt-5 border-t border-[#E5E1D2]">
              <p className="text-[11px] uppercase tracking-wider text-gold font-semibold mb-3">{EN ? "Destinations we recommend for you" : "Le destinazioni che ti consigliamo"}</p>
              {results.length === 0 ? (
                <p className="text-sm text-ink/60">{EN ? "No exact match. Tell your concierge directly — we'll quietly find a perfect fit." : "Nessun match diretto. Parla con il consulente: troveremo la soluzione giusta per te."}</p>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3">
                  {results.map((r, idx) => (
                    <Link
                      key={r.id}
                      to={localizePath(`/${EN ? "destinations" : "destinazioni"}/${r.slug}`)}
                      data-testid={`match-result-${r.slug}`}
                      className="group block rounded-xl border border-[#E2E8F0] bg-white overflow-hidden hover:border-navy/40 hover:shadow-md transition relative"
                    >
                      <div className="absolute top-2 left-2 z-10 h-8 min-w-[2rem] px-2 rounded-full bg-gold text-navy text-xs font-bold flex items-center justify-center shadow-sm">
                        {RANK_LABELS[idx] || `${idx + 1}°`}
                      </div>
                      <div className="relative h-32 overflow-hidden bg-sand">
                        <img src={r.hero_image} alt={r.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                        <p className="absolute bottom-2 left-3 right-3 font-serif text-base text-white">{r.name}</p>
                      </div>
                      <div className="p-3">
                        <p className="text-xs text-ink/70 line-clamp-2">{r.type_label}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              <Button
                data-testid="match-cta-search"
                onClick={() => navigate(localizePath("/search"))}
                className="mt-5 bg-navy text-white hover:bg-[#06101f] rounded-full h-11 px-6 text-sm font-semibold"
              >
                {EN ? "Get a tailored proposal" : "Ricevi una proposta personalizzata"}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
